import { ReactNode, Ref, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Folder, Tag, CalendarDays, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { filterAttachmentsForCarousel } from '@/redux/post/post.utils';
import Loader from '@/components/common/Loader';
import ProfilePicture from '@/components/ui/profile-picture';
import DescriptionWithLinks from '@/components/Post/DescriptionWithLinks';
import DifficultyBadge from '@/components/Post/DifficultyBadge';
import { Separator } from '@/components/ui/separator';
import GroupPartsDisplay from './GroupPartsDisplay';
import MemberProjectsList from './MemberProjectsList';
import PostDetailCarousel from '@/components/Post/PostDetailCarousel';
import PostStatusBadge from '@/components/Post/PostStatusBadge';
import { Toggle } from '@/components/ui/toggle';
import {
  IPostVisibility,
  IPostAttachment,
  IAttachmentCategory,
  IModelGroup,
} from '@/redux/post/post.d';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import type {
  IProjectGroupLight,
  IProjectGroupWithDetails,
} from '@/redux/projectGroup/projectGroup.d';

const STAMP: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

interface ProjectGroupBodyProps {
  group: IProjectGroupLight | IProjectGroupWithDetails;
  /**
   * The project whose attachments feed the carousel. The detail page fetches it
   * on demand; the admin preview already has it in the group payload.
   */
  currentProject?: { attachments?: IPostAttachment[] };
  isProjectLoading?: boolean;
  selectedProjectId: string;
  onSelectProject: (projectId: string) => void;
  /** Parts are deferred on the detail page and inline in the preview. */
  parts?: {
    aggregatedModelGroups?: IModelGroup[];
    totalPartsCount?: number;
  };
  isPartsLoading?: boolean;
  /** Detail page only: what the intersection observer watches to fetch parts. */
  partsRef?: Ref<HTMLDivElement>;
  /** Owner-only menu, beside the status badge. */
  titleActions?: ReactNode;
  /** Like / bookmark / share. Absent in the token-authed preview. */
  interactions?: ReactNode;
  /** Download control, pinned right of the interactions. */
  download?: ReactNode;
  /** Admin-feedback opener, on its own row above the interactions. */
  notice?: ReactNode;
}

/**
 * The whole two-column group body: cover and project switcher on the left,
 * action bar, description and parts on the right.
 *
 * Shared by the public group page and the admin preview so the two cannot
 * drift — they had, and the preview was missing the action bar, the category,
 * the skill level and the dates.
 */
// fallow-ignore-next-line complexity
export default function ProjectGroupBody({
  group,
  currentProject,
  isProjectLoading,
  selectedProjectId,
  onSelectProject,
  parts,
  isPartsLoading,
  partsRef,
  titleActions,
  interactions,
  download,
  notice,
}: ProjectGroupBodyProps) {
  const [modelIndex, setModelIndex] = useState<number>(0);

  useEffect(() => {
    setModelIndex(0);
  }, [selectedProjectId]);

  const sortedTags = useMemo(() => {
    if (!group?.tags) return [];
    return [...group.tags].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [group?.tags]);

  const categoryName =
    typeof group.category === 'string' ? group.category : group.category?.name;

  // fallow-ignore-next-line complexity
  const carouselAttachments = useMemo(() => {
    const attachments: IPostAttachment[] = [];

    attachments.push({
      _id: group?.thumbnail?.url
        ? 'group-thumbnail'
        : 'group-thumbnail-placeholder',
      url:
        group?.thumbnail?.url ??
        'https://via.placeholder.com/800x400?text=Project+Group',
      fileName: group?.thumbnail?.fileName || 'Group Cover.png',
      resourceType: AttachmentType.image,
      category: 'image',
    } as unknown as IPostAttachment);

    if (currentProject?.attachments) {
      // fallow-ignore-next-line complexity
      const sorted = [...currentProject.attachments].sort((a, b) => {
        const aIsThumb = a.category === IAttachmentCategory.postThumbnail;
        const bIsThumb = b.category === IAttachmentCategory.postThumbnail;
        if (aIsThumb && !bIsThumb) return -1;
        if (!aIsThumb && bIsThumb) return 1;

        const aIsImage = a.resourceType === AttachmentType.image;
        const bIsImage = b.resourceType === AttachmentType.image;
        if (aIsImage && !bIsImage) return -1;
        if (!aIsImage && bIsImage) return 1;

        return 0;
      });
      attachments.push(...sorted);
    }

    return attachments;
  }, [group, currentProject]);

  return (
    <div className="mx-auto rounded-none border-none shadow-none px-3 sm:px-5 py-4 sm:py-8">
      <div className="flex flex-wrap gap-4 lg:gap-0 lg:flex-nowrap">
        <div className="flex flex-col lg:w-[50%] lg:border-r-[1px] border-[#E0E0E0] items-center justify-start px-0 lg:px-8 w-full border-r-0">
          {/* Header mirrors PostDetail: title + status/menu, author row below. */}
          <div className="flex flex-col w-full gap-2 mb-4">
            <div className="flex items-start justify-between gap-2">
              <h1 className="font-primary font-bold text-2xl capitalize">
                {group.name}
              </h1>
              <div className="flex items-center gap-2 shrink-0">
                <PostStatusBadge
                  visibility={group.visibility as IPostVisibility}
                />
                {titleActions}
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Link
                to={group?.user?.username ? `/${group.user.username}` : '#'}
              >
                <div className="flex items-center gap-2">
                  <ProfilePicture
                    src={group?.user?.profilePicture}
                    alt={
                      group?.user?.firstname || group?.user?.username || 'User'
                    }
                    username={group?.user?.username}
                    variant="badge"
                    size="sm"
                  />
                  <span className="text-sm text-[#666666]">
                    @{group?.user?.username || 'unknown'}
                  </span>
                </div>
              </Link>
              <span className="flex items-center gap-2 text-sm text-primary">
                <Folder className="h-4 w-4" />
                Multi Workpiece
              </span>
            </div>
          </div>

          <Separator className="lg:hidden mb-4" />

          <div className="flex gap-2 items-center justify-center flex-wrap mb-4 w-full">
            {group.projects.map((project) => (
              <Toggle
                key={project._id}
                pressed={selectedProjectId === project._id}
                onPressedChange={() => onSelectProject(project._id)}
                className="gap-2 border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
              >
                <span
                  className="max-w-[200px] truncate block text-center"
                  title={project.title}
                >
                  {project.title}
                </span>
              </Toggle>
            ))}
          </div>

          <div className="w-full relative rounded-lg overflow-hidden">
            {isProjectLoading ? (
              <div className="flex justify-center items-center h-[400px]">
                <Loader title="Loading project details..." description="" />
              </div>
            ) : (
              <PostDetailCarousel
                attachments={filterAttachmentsForCarousel(carouselAttachments)}
                modelIndex={modelIndex}
                setModelIndex={setModelIndex}
              />
            )}
          </div>
        </div>

        <div className="lg:h-[650px] lg:overflow-y-auto lg:w-[50%] w-full custom-scrollbar relative">
          {/* Action bar mirrors PostDetail's: actions row, then category + dates. */}
          <div className="sticky top-0 z-10 px-0 lg:px-[35px] py-3 mb-2">
            <Card className="flex flex-col gap-3 p-3 bg-transparent backdrop-blur">
              <div className="flex flex-wrap items-center gap-2 empty:hidden">
                {notice}
              </div>
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center">
                {/* ponytail: one wrapper strips the children's own borders so they read as a single segmented group */}
                {/* Mirrors the post detail card: the labels never fit one
                    375px row, and auto-fit copes with any button count. */}
                <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-0.5 rounded-2xl border bg-white/50 p-0.5 [&>*:last-child:nth-child(odd)]:col-span-full [&_button]:w-auto [&_button]:rounded-full [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-6 [&_button]:text-sm [&_button]:shadow-none [&_button:hover]:bg-white empty:hidden sm:flex sm:w-auto sm:items-center sm:rounded-full">
                  {interactions}
                </div>
                <div className="w-full sm:ml-auto sm:w-auto">{download}</div>
              </div>
              <div className="flex justify-between items-center flex-wrap gap-2 w-full">
                <div className="flex flex-wrap items-center gap-2 empty:hidden">
                  {categoryName && (
                    <Link
                      to={`/?category=${encodeURIComponent(categoryName)}`}
                      title="Browse this category"
                    >
                      <Badge
                        variant="secondary"
                        className="gap-1.5 rounded-full px-3 py-1 font-medium capitalize cursor-pointer border border-border transition-colors hover:border-primary/60 hover:text-primary hover:bg-primary/5"
                      >
                        <Tag className="h-3.5 w-3.5" />
                        {categoryName}
                      </Badge>
                    </Link>
                  )}
                  <DifficultyBadge difficulty={group.difficulty} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    Uploaded{' '}
                    {new Date(group.createdAt).toLocaleString('en-US', STAMP)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Pencil className="h-3.5 w-3.5" />
                    Edited{' '}
                    {new Date(
                      group.updatedAt || group.createdAt
                    ).toLocaleString('en-US', STAMP)}
                  </span>
                </div>
              </div>
            </Card>
          </div>
          <div className="px-0 lg:px-[35px]">
            {group.description && (
              <>
                <DescriptionWithLinks
                  description={group.description}
                  footer={
                    sortedTags.length > 0 && (
                      <p className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-blue-500">
                        {sortedTags.map((tag) => (
                          <span
                            key={tag._id}
                            className="cursor-pointer hover:underline"
                          >
                            #{tag.name.replace(/\s+/g, '')}
                          </span>
                        ))}
                      </p>
                    )
                  }
                />
                <Separator className="my-4" />
              </>
            )}

            <MemberProjectsList projects={group.projects} />

            <div ref={partsRef}>
              {parts?.aggregatedModelGroups && (
                <GroupPartsDisplay
                  aggregatedModelGroups={parts.aggregatedModelGroups}
                  projects={group.projects}
                  totalPartsCount={parts.totalPartsCount || 0}
                  modelIndex={modelIndex}
                  setModelIndex={setModelIndex}
                  carouselAttachments={carouselAttachments}
                />
              )}
              {isPartsLoading && (
                <div className="flex justify-center items-center p-8">
                  <Loader title="Loading parts data..." description="" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
